import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { GetProjects, addProjects } from "../../indexDB/utils/project";
import React, { useCallback, useEffect, useState } from "react";
import { getDataset, getDatasetByUUID } from "../../indexDB/utils/dataset";

import { getAllDataPoints } from "../../api/datapoint";
import { handleApiError } from "../errorBoundary/errorUtils";
import { useErrorBoundary } from "react-error-boundary";
import { useNavigate } from "react-router-dom";

const SyncDatapoint = ({ handleLogout }) => {
  const { showBoundary } = useErrorBoundary();

  const navigate = useNavigate();
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [projects, setProjects] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [customHeadings, setCustomHeadings] = useState([]);
  const [loading, setLoading] = useState(false);

  const [tableData, setTableData] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loadingPresignedURL, setLoadingPresignedURL] = useState(false);

  const fetchProjects = async () => {
    try {
      const indexDBProjects = await GetProjects();
      console.log(indexDBProjects);
      setProjects(indexDBProjects);
    } catch (error) {
      console.error("Error fetching data from the indexDB");
    }
  };

  const fetchDatasets = async (project) => {
    try {
      const offlineDatasets = await getDataset();
      console.log(offlineDatasets)
      setDatasets(offlineDatasets);
    } catch (error) {
      showBoundary(error);
    }
  };

  const fetchTableEntries = async () => {
    try {
      if (selectedDataset) {
        setLoading(true);
        const result = await getAllDataPoints(
          selectedDataset.uuid,
          rowsPerPage,
          page,
          "created_at",
          "desc",
          handleLogout
        );
        const entries = result?.results;
        setCount(result?.count ? result.count : 0);
        const entriesWithData = await Promise.all(
          entries?.map(async (entry) => {
            const dataset = await getDatasetByUUID(entry.dataset);

            const showLabels = dataset?.schema?.show_labels || [];
            setCustomHeadings(getLastItems(showLabels));
            let showLabel1, showLabel2;

            if (showLabels.length > 0) {
              const pathParts1 = showLabels[0].split(".");
              showLabel1 = traverseFormData(entry.data, pathParts1);
            }

            if (showLabels.length > 1) {
              const pathParts2 = showLabels[1].split(".");
              showLabel2 = traverseFormData(entry.data, pathParts2);
            }

            return {
              ...entry,
              showLabel1: showLabel1,
              showLabel2: showLabel2,
            };
          })
        );
        setTableData(entriesWithData);
        setLoading(false);
      }
    } catch (error) {
      showBoundary(error);
    }
  };

  function getLastItems(stringsArray) {
    return stringsArray.map((str) => {
      const parts = str.split(".");
      return parts[parts.length - 1];
    });
  }

  const traverseFormData = (formData, pathParts) => {
    return pathParts.reduce(
      (obj, key) => (obj && obj[key] !== "undefined" ? obj[key] : undefined),
      formData
    );
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchDatasets(selectedProject);
    }
  }, [selectedProject]);

  useEffect(() => {
    fetchTableEntries();
  }, [page, rowsPerPage, selectedDataset]);

  const handleProjectChange = (event) => {
    const project = event.target.value;
    setSelectedProject(project);
    localStorage.setItem("project", event.target.value.name);
    localStorage.setItem("project_id", event.target.value.id);
    setSelectedDataset(null);
  };

  const handleDatasetChange = (event) => {
    const dataset = event.target.value;
    localStorage.setItem("dataset_id", event.target.value.id);
    setSelectedDataset(dataset);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleBackButtonClick = () => {
    setPage(page - 1);
  };

  const handleNextButtonClick = () => {
    setPage(page + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleItemClick = useCallback(
    async (row) => {
      setLoadingPresignedURL(true);
      try {
        navigate("/view-form", {
          state: {
            datasetID: row.dataset,
            draftId: row.draft_id,
            formData: row.data,
            files: row.files,
            id: row.id,
            status: "online",
          },
        });
      } catch (error) {
        console.error("Error in handleItemClick:", error);
      } finally {
        setLoadingPresignedURL(false);
      }
    }, [navigate]
  );

  const formateDatetime = (datetime) => {
    try {
      const date = new Date(datetime);
      const formattedDate = date.toLocaleString('en-US', {
        // weekday: 'long',    // "Friday"
        day: 'numeric',     // "23"
        month: 'long',      // "August"
        year: 'numeric',    // "2024"
        hour: 'numeric',    // "7 AM"
        minute: 'numeric',  // "21"
        second: 'numeric',  // "52"
        hour12: true        // 12-hour format
      });
      return formattedDate;
    } catch {
      console.log("failed to formate datetime")
      return datetime
    }
  }

  return (
    <Box
      sx={{
        minHeight: `calc(100vh - 64px)`,
        width: "100%",
        padding: { xs: "12px", sm: "40px" },
      }}
    >
      <Typography variant="h5" mb={2}>
        Synced Datapoints
      </Typography>
      <Box display="flex" alignItems="center" mb={2}>
        <FormControl
          fullWidth
          variant="outlined"
          style={{ marginRight: "16px" }}
        >
          <InputLabel id="project-select-label">Select Project</InputLabel>
          <Select
            labelId="project-select-label"
            id="project-select"
            value={selectedProject}
            onChange={handleProjectChange}
            label="Select Project"
          >
            {projects?.map((project) => (
              <MenuItem
                key={project.id}
                selected={project.name === selectedProject}
                value={project}
              >
                {project.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth variant="outlined">
          <InputLabel id="dataset-select-label" disabled={!selectedProject}>
            Select Datasets
          </InputLabel>
          <Select
            labelId="dataset-select-label"
            id="dataset-select"
            value={selectedDataset}
            onChange={handleDatasetChange}
            label="Select Dataset"
            disabled={!selectedProject}
          >
            {datasets?.map((dataset) => (
              <MenuItem
                key={dataset.id}
                selected={dataset.name === selectedDataset}
                value={dataset}
              >
                {dataset.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <TableContainer
        component={Paper}
        style={{ maxHeight: "calc(100vh - 240px)", overflow: "auto" }}
      >
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Synced Datetime</TableCell>
              {customHeadings?.map((heading, index) => (
                <TableCell key={index}>{heading}</TableCell>
              ))}
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody
            style={{
              overflowY: "auto",
              maxHeight: "30%",
            }}
          >
            {loading ? (
              Array.from({ length: rowsPerPage }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Skeleton animation="wave" />
                  </TableCell>
                  <TableCell>
                    <Skeleton animation="wave" />
                  </TableCell>
                  <TableCell>
                    <Skeleton animation="wave" />
                  </TableCell>
                  {customHeadings.length > 0 && <TableCell>
                    <Skeleton animation="wave" />
                  </TableCell>}
                  {customHeadings.length > 1 && <TableCell>
                    <Skeleton animation="wave" />
                  </TableCell>}
                </TableRow>
              ))
            ) : (
              <>
                <Dialog open={loadingPresignedURL}>
                  <DialogTitle sx={{ textAlign: "center" }}>
                    Loading Record
                  </DialogTitle>
                  <DialogContent
                    dividers
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <CircularProgress size="64px" sx={{ my: 2 }} />
                    <Typography variant="body1" align="center">
                      Preparing files for viewing...
                    </Typography>
                  </DialogContent>
                </Dialog>
                {tableData.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.draft_id || row.id}</TableCell>
                    <TableCell>{formateDatetime(row?.created_at)}</TableCell>
                    {customHeadings.length > 0 && <TableCell>{row.showLabel1}</TableCell>}
                    {customHeadings.length > 1 && <TableCell>{row.showLabel2}</TableCell>}
                    <TableCell>
                      <Button onClick={() => handleItemClick(row)}> View </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        sx={{
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            margin: 0,
          },
        }}
        rowsPerPageOptions={[5, 10, 25]}
        colSpan={3}
        SelectProps={{
          inputProps: {
            "aria-label": "rows per page",
          },
          native: true,
        }}
        count={count ? count : 0}
        rowsPerPage={rowsPerPage ? rowsPerPage : 10}
        page={page ? page : 0}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
};

export default SyncDatapoint;
